import React, { useState } from 'react';
import { adminApi, tutorApi } from '../services/api';
import jjtLogo from '../assets/images/jj-tutorial-logo.png.png';
import '../assets/InvoicePreview.css';

const InvoicePreview = ({ invoiceType, recipient, month, year, invoiceData, templateSettings, onClose, isTutorView = false }) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  
  // Email state
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', { 
      timeZone: 'Asia/Singapore',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  // Hide blanks and known placeholder contact values (incl. legacy ones)
  const isRealContact = (v) => {
    if (!v) return false;
    const s = String(v).trim().toLowerCase();
    if (!s) return false;
    return !['n/a', 'na', '-', 'none', 'nil', 'null', 'undefined',
      'parent@example.com', '00000000'].includes(s);
  };

  const generateInvoiceNumber = () => {
    const prefix = invoiceType === 'tutor' ? 'TUT' : 'TEE';
    const yearShort = year.toString().slice(-2);
    const monthPadded = month.toString().padStart(2, '0');
    const recipientId = recipient._id ? recipient._id.slice(-6).toUpperCase() : '000000';
    return `${prefix}-${yearShort}${monthPadded}-${recipientId}`;
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    setExportError('');
    setExportSuccess(false);

    try {
      let response;
      
      if (invoiceType === 'tutee') {
        // For tutee invoices, use the invoice ID to download PDF
        if (!invoiceData.invoiceId && !recipient._id) {
          throw new Error('Invoice ID is required for tutee invoice PDF export');
        }
        const invoiceId = invoiceData.invoiceId || recipient._id;
        response = await adminApi.downloadTuteeInvoicePDF(invoiceId);
      } else if (isTutorView) {
        // Use tutor API for tutors viewing their own invoices
        response = await tutorApi.exportMyInvoicePDF(month, year);
      } else {
        // Use admin API for tutor invoices
        response = await adminApi.exportInvoicePDF({
          invoiceType,
          recipientId: recipient._id,
          month,
          year
        });
      }

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const sanitizedName = recipient.name.replace(/[^a-zA-Z0-9]/g, '_');
      
      if (invoiceType === 'tutee') {
        // Format: invoice_tutee_TUTEECODE_tuteename_MM_YYYY
        const monthNames = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
        const monthStr = monthNames[month - 1];
        const tuteeCode = recipient.code ? recipient.code.toUpperCase().replace(/[^A-Z0-9\-]/g, '_') + '_' : '';
        link.download = `invoice_tutee_${tuteeCode}${sanitizedName}_${monthStr}_${year}.pdf`;
      } else {
        // For tutor invoices, use invoice number
        const invoiceNum = invoiceData.invoiceNumber || `${month}_${year}`;
        link.download = `Invoice_${invoiceNum}_${sanitizedName}.pdf`;
      }
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setExportSuccess(false);
      }, 3000);
      
      // Call onClose callback if provided after successful export
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setExportError(
        error.response?.data?.message || 
        'Failed to export PDF. Please try again.'
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleEmailClick = () => {
    // Clear previous messages
    setEmailError('');
    setEmailSuccess(false);
    // Show confirmation dialog
    setShowEmailConfirmation(true);
  };

  const handleEmailConfirm = async () => {
    setShowEmailConfirmation(false);
    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      const response = await adminApi.emailInvoice({
        invoiceType,
        recipientId: recipient._id,
        month,
        year
      });

      setEmailSuccess(true);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setEmailSuccess(false);
      }, 5000);
      
      // Call onClose callback if provided after successful email
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error sending invoice email:', error);
      setEmailError(
        error.response?.data?.message || 
        'Failed to send invoice email. Please try again.'
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailCancel = () => {
    setShowEmailConfirmation(false);
  };

  return (
    <div className="invoice-preview-container">
      {/* Company Letterhead — minimal: logo + name left, invoice meta right */}
      <div className="invoice-header-section">
        <div className="company-branding">
          <img src={jjtLogo} alt="JJ Tutorial" className="company-logo" />
          <div className="company-branding-text">
            <h1 className="company-name">
              {templateSettings?.headerText || 'JJ Tutorial Singapore'}
            </h1>
            {(templateSettings?.companyEmail || templateSettings?.companyPhone) && (
              <p className="company-contact">
                {[templateSettings?.companyEmail, templateSettings?.companyPhone]
                  .filter(Boolean)
                  .join('  ·  ')}
              </p>
            )}
          </div>
        </div>
        <div className="invoice-meta-box">
          <span className="invoice-type-label">
            {invoiceType === 'tutor' ? 'Tutor Invoice' : 'Tutee Invoice'}
          </span>
          <div className="meta-line">
            <span className="meta-label">Invoice No.</span>
            <span className="meta-value">{invoiceData.invoiceNumber || generateInvoiceNumber()}</span>
          </div>
          <div className="meta-line">
            <span className="meta-label">Date Issued</span>
            <span className="meta-value">{getCurrentDate()}</span>
          </div>
          <div className="meta-line">
            <span className="meta-label">Billing Period</span>
            <span className="meta-value">{months[month - 1]} {year}</span>
          </div>
        </div>
      </div>

      {/* Bill To + Student (same block) */}
      <div className="invoice-parties-row">
        <div className="recipient-section">
          <h3>Bill To</h3>
          <p className="recipient-name">{recipient.name}</p>
          {isRealContact(recipient.email) && <p className="recipient-email">{recipient.email}</p>}
          {isRealContact(recipient.phone) && <p className="recipient-phone">{recipient.phone}</p>}
        </div>
        {invoiceType === 'tutee' && (invoiceData.tutee?.name || invoiceData.studentName) && (
          <div className="student-section">
            <h3>Student</h3>
            <p className="student-name">{invoiceData.tutee?.name || invoiceData.studentName}</p>
          </div>
        )}
      </div>

      {/* Lessons Table */}
      <div className="lessons-section">
        <h3>Lessons {invoiceType === 'tutor' ? 'Taught' : 'Received'}</h3>
        {invoiceData.lessons && invoiceData.lessons.length > 0 ? (
          <table className="invoice-lessons-table">
            <thead>
              <tr>
                <th className="col-date">Date & Time</th>
                <th className="col-name">{invoiceType === 'tutor' ? 'Tutee' : 'Tutor'}</th>
                <th className="col-subject">Subject</th>
                <th className="col-duration">Duration</th>
                <th className="col-rate">Rate</th>
                <th className="col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {[...invoiceData.lessons].sort((a, b) => {
                // Sort by tutee/tutor name first
                const nameA = invoiceType === 'tutor' 
                  ? (a.tutee?.name || '').toLowerCase()
                  : (a.tutor?.name || '').toLowerCase();
                const nameB = invoiceType === 'tutor'
                  ? (b.tutee?.name || '').toLowerCase()
                  : (b.tutor?.name || '').toLowerCase();
                
                if (nameA !== nameB) {
                  return nameA.localeCompare(nameB);
                }
                
                // Then by subject
                const subjectA = (a.subject || '').toLowerCase();
                const subjectB = (b.subject || '').toLowerCase();
                
                if (subjectA !== subjectB) {
                  return subjectA.localeCompare(subjectB);
                }
                
                // Finally by date/time
                return new Date(a.date) - new Date(b.date);
              }).map((lesson, index) => {
                const hours = lesson.duration / 60;
                const rate = invoiceType === 'tutor' ? lesson.tutorRate : lesson.rate;
                const amount = rate * hours;
                
                return (
                  <tr key={index}>
                    <td className="col-date">{formatDate(lesson.date)}</td>
                    <td className="col-name">
                      {invoiceType === 'tutor' 
                        ? (lesson.tutee?.name || 'N/A')
                        : (lesson.tutor?.name || 'N/A')}
                    </td>
                    <td className="col-subject">{lesson.subject}</td>
                    <td className="col-duration">{lesson.duration} min</td>
                    <td className="col-rate">{formatCurrency(rate)}/hr</td>
                    <td className="col-amount positive">+{formatCurrency(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td colSpan="5" className="subtotal-label">Lessons Subtotal</td>
                <td className="subtotal-amount positive">
                  +{formatCurrency(invoiceData.totals?.lessonEarnings || invoiceData.lessonEarnings || 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="no-lessons-message">
            <p>No lessons found for this period</p>
          </div>
        )}
      </div>

      {/* Tutee Invoice Adjustments Section */}
      {invoiceType === 'tutee' && invoiceData.adjustments && invoiceData.adjustments.length > 0 && (
        <div className="adjustments-section">
          <h3>Adjustments</h3>
          <table className="adjustments-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.adjustments.map((adjustment, index) => (
                <tr key={index}>
                  <td className="adjustment-category">
                    {adjustment.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </td>
                  <td className="adjustment-description">{adjustment.description}</td>
                  <td className={`adjustment-amount ${adjustment.amount >= 0 ? 'positive' : 'negative'}`}>
                    {adjustment.amount >= 0 ? '+' : ''}
                    {formatCurrency(adjustment.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td colSpan="2" className="subtotal-label">Adjustments Total</td>
                <td className={`subtotal-amount ${(invoiceData.adjustments.reduce((sum, adj) => sum + adj.amount, 0)) >= 0 ? 'positive' : 'negative'}`}>
                  {(invoiceData.adjustments.reduce((sum, adj) => sum + adj.amount, 0)) >= 0 ? '+' : ''}
                  {formatCurrency(invoiceData.adjustments.reduce((sum, adj) => sum + adj.amount, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Tutee Invoice Discounts Section */}
      {invoiceType === 'tutee' && invoiceData.discounts && invoiceData.discounts.length > 0 && (
        <div className="discounts-section">
          <h3>Discounts</h3>
          <table className="discounts-table">
            <thead>
              <tr>
                <th>Reason</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.discounts.map((discount, index) => (
                <tr key={index}>
                  <td className="discount-reason">{discount.reason}</td>
                  <td className="discount-amount negative">
                    -{formatCurrency(discount.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td className="subtotal-label">Discounts Total</td>
                <td className="subtotal-amount negative">
                  -{formatCurrency(invoiceData.discounts.reduce((sum, disc) => sum + disc.amount, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Payment Adjustments Section */}
      {invoiceData.adjustments && invoiceData.adjustments.length > 0 && (
        <div className="adjustments-section">
          <h3>Payment Adjustments</h3>
          <table className="invoice-adjustments-table">
            <thead>
              <tr>
                <th className="col-type">Type</th>
                <th className="col-reason">Reason</th>
                <th className="col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.adjustments.map((adjustment, index) => {
                // For profit share deductions, use viewerAmount if available
                const displayAmount = adjustment.viewerAmount !== undefined ? adjustment.viewerAmount : adjustment.amount;
                const recipientInfo = adjustment.isProfitShareDeduction && adjustment.tutor?.name 
                  ? ` (to ${adjustment.tutor.name})` 
                  : '';
                
                return (
                  <tr key={index}>
                    <td className="col-type">{adjustment.type}</td>
                    <td className="col-reason">{adjustment.reason}{recipientInfo}</td>
                    <td className={`col-amount ${displayAmount >= 0 ? 'positive' : 'negative'}`}>
                      {displayAmount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(displayAmount))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td colSpan="2" className="subtotal-label">Adjustments Total</td>
                <td className={`subtotal-amount ${invoiceData.totals?.adjustmentTotal >= 0 ? 'positive' : 'negative'}`}>
                  {invoiceData.totals?.adjustmentTotal >= 0 ? '+' : '-'}
                  {formatCurrency(Math.abs(invoiceData.totals?.adjustmentTotal || 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Recurring Payments Section */}
      {invoiceData.recurringPayments && invoiceData.recurringPayments.length > 0 && (
        <div className="recurring-payments-section">
          <h3>Recurring Payments</h3>
          <table className="invoice-recurring-table">
            <thead>
              <tr>
                <th className="col-type">Type</th>
                <th className="col-reason">Reason</th>
                <th className="col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.recurringPayments.map((payment, index) => {
                // For profit share deductions, use viewerAmount if available
                const displayAmount = payment.viewerAmount !== undefined ? payment.viewerAmount : payment.amount;
                const recipientInfo = payment.isRecipientProfitSharer && payment.tutor?.name 
                  ? ` (to ${payment.tutor.name})` 
                  : '';
                
                return (
                  <tr key={index}>
                    <td className="col-type">{payment.type}</td>
                    <td className="col-reason">{payment.reason}{recipientInfo}</td>
                    <td className={`col-amount ${displayAmount >= 0 ? 'positive' : 'negative'}`}>
                      {displayAmount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(displayAmount))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td colSpan="2" className="subtotal-label">Recurring Payments Total</td>
                <td className={`subtotal-amount ${invoiceData.totals?.recurringTotal >= 0 ? 'positive' : 'negative'}`}>
                  {invoiceData.totals?.recurringTotal >= 0 ? '+' : '-'}
                  {formatCurrency(Math.abs(invoiceData.totals?.recurringTotal || 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Totals Summary */}
      <div className="totals-summary-section">
        <div className="totals-breakdown">
          <div className="totals-row">
            <span className="totals-label">Subtotal (Lessons):</span>
            <span className="totals-value positive">
              +{formatCurrency(invoiceData.totals?.lessonEarnings || invoiceData.lessonEarnings || 0)}
            </span>
          </div>
          
          {invoiceData.totals?.adjustmentTotal !== 0 && (
            <div className="totals-row">
              <span className="totals-label">Adjustments:</span>
              <span className={`totals-value ${invoiceData.totals?.adjustmentTotal >= 0 ? 'positive' : 'negative'}`}>
                {invoiceData.totals?.adjustmentTotal >= 0 ? '+' : '-'}
                {formatCurrency(Math.abs(invoiceData.totals?.adjustmentTotal || 0))}
              </span>
            </div>
          )}
          
          {invoiceData.totals?.recurringTotal !== 0 && (
            <div className="totals-row">
              <span className="totals-label">Recurring Payments:</span>
              <span className={`totals-value ${invoiceData.totals?.recurringTotal >= 0 ? 'positive' : 'negative'}`}>
                {invoiceData.totals?.recurringTotal >= 0 ? '+' : '-'}
                {formatCurrency(Math.abs(invoiceData.totals?.recurringTotal || 0))}
              </span>
            </div>
          )}
          
          {invoiceData.profitShare && invoiceData.profitShare.grossProfitShare !== 0 && (
            <div className="totals-row profit-share-row">
              <span className="totals-label">Commission Earnings (Profit Share):</span>
              <span className={`totals-value ${invoiceData.profitShare.grossProfitShare >= 0 ? 'positive' : 'negative'}`}>
                {invoiceData.profitShare.grossProfitShare >= 0 ? '+' : '-'}
                {formatCurrency(Math.abs(invoiceData.profitShare.grossProfitShare))}
              </span>
            </div>
          )}
          
          <div className="totals-row grand-total">
            <span className="totals-label">
              Total {invoiceType === 'tutor' ? 'Payment' : 'Amount Due'}:
            </span>
            <span className="totals-value">
              {formatCurrency(invoiceData.totals?.finalAmount || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Instructions Footer - Only for tutor invoices */}
      {invoiceType === 'tutor' && (
        <div className="payment-instructions-section">
          <h3>Payment Instructions</h3>
          <div className="instructions-content">
            <p>Payment will be processed via bank transfer to your registered account.</p>
            <p><strong>Payment Date:</strong> 15th of the following month</p>
            <p>If you have any questions regarding this invoice, please contact the administration office.</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="invoice-footer-section">
        <p className="footer-text">
          {templateSettings?.footerText || 'Thank you for your business!'}
        </p>
        <p className="footer-contact">
          {[
            templateSettings?.headerText || 'JJ Tutorial Singapore',
            templateSettings?.companyEmail && `Email: ${templateSettings.companyEmail}`,
            templateSettings?.companyPhone && `Phone: ${templateSettings.companyPhone}`
          ].filter(Boolean).join('  |  ')}
        </p>
      </div>

      {/* Export Actions */}
      <div className="invoice-export-actions">
        <button 
          className="export-pdf-btn"
          onClick={handleExportPDF}
          disabled={exportLoading || emailLoading}
        >
          {exportLoading ? (
            <>
              <span className="btn-spinner"></span>
              Generating PDF...
            </>
          ) : (
            <>
              <span className="btn-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
                </svg>
              </span>
              Export as PDF
            </>
          )}
        </button>

        {/* Email Invoice button removed — tutee invoices are sent via WhatsApp,
            not email, so this action is unused. */}

        {exportSuccess && (
          <div className="export-success-message">
            ✓ PDF downloaded successfully!
          </div>
        )}

        {exportError && (
          <div className="export-error-message">
            ✗ {exportError}
          </div>
        )}

        {!isTutorView && emailSuccess && (
          <div className="email-success-message">
            ✓ Invoice email sent successfully to {recipient.email}!
          </div>
        )}

        {!isTutorView && emailError && (
          <div className="email-error-message">
            ✗ {emailError}
          </div>
        )}
      </div>

      {/* Email Confirmation Dialog */}
      {!isTutorView && showEmailConfirmation && (
        <div className="email-confirmation-overlay">
          <div className="email-confirmation-dialog">
            <div className="confirmation-header">
              <h3>Confirm Email Delivery</h3>
            </div>
            <div className="confirmation-body">
              <p>Are you sure you want to email this invoice to:</p>
              <div className="recipient-info-box">
                <p className="recipient-name-confirm">{recipient.name}</p>
                <p className="recipient-email-confirm">{recipient.email || (invoiceType === 'tutee' ? recipient.parentEmail : 'No email available')}</p>
              </div>
              <p className="confirmation-note">
                The invoice will be sent as a PDF attachment.
              </p>
            </div>
            <div className="confirmation-actions">
              <button 
                className="cancel-btn"
                onClick={handleEmailCancel}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn"
                onClick={handleEmailConfirm}
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePreview;
